/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useRef } from "react";

import Head from "next/head";
import { useRouter } from "next/router";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { Layer } from "@vienna/common";

import { Modal } from "../UI/Modal/Modal";
import { DEVICE } from "src/app/globalStyles";
import { Plate } from "src/classes/plate/Plate";
import { DrilledView } from "src/components/DrilledView/DrilledView";
import { ScrollContainer } from "src/components/Layout/PageLayout";
import { Blocks } from "src/components/Plate/Blocks";
import { Canvas } from "src/components/Plate/Canvas/Canvas";
import { DragListener } from "src/components/Plate/Canvas/DragListener";
import { Internals } from "src/components/Plate/Data";
import { HelpMenu } from "src/components/Plate/HelpMenu/HelpMenu";
import { PlateTopbar } from "src/components/Plate/Topbar/PlateTopbar";
import { ZoomWrapper } from "src/components/Plate/ZoomWrapper/ZoomWrapper";
import { StandardButton } from "src/components/UI/Buttons";
import { ConditionalWrapper } from "src/components/UI/ConditionalWrapper";
import { Cursors } from "src/components/UI/Cursors";
import { Uploader } from "src/components/Uploader/Uploader";
import { PLATE_PADDING } from "src/lib/constants";
import { useMouseEvent } from "src/lib/hooks/useMouseEvent";
import { usePlateShortcuts } from "src/lib/hooks/usePlateShortcuts";
import { useScroll } from "src/lib/hooks/useScroll";
import { useWindowResize } from "src/lib/hooks/useWindowResize";
import { AppState } from "src/store";

interface Props {
  plate: Plate;
}
interface NextQuery {
  blockId: string;
  pageId: string;
}

export function PlateView({ plate }: Props) {
  const editable = plate.observe((state) => state.editable);
  const title = plate.observe((state) => state.title);
  const modal = plate.observe((state) => state.modal);

  const router = useRouter();
  const { blockId: drilledBlockId, pageId: drilledPageId }: NextQuery = router.query as any as NextQuery;

  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const isScreenshotter = useSelector((state: AppState) => state.globalUI.uiSettings.isScreenshotter);

  useEffect(() => {
    return () => {
      plate.destroy();
    };
  }, []);

  usePlateShortcuts(plate);
  useMouseEvent("mousemove", (e) => plate.onMouseMove(e));
  useMouseEvent("mouseup", () => plate.onMouseUp());
  useScroll(() => plate.mouseTracker.onScroll(canvasRef));
  useWindowResize(() => plate.mouseTracker.onResizeWindow(canvasRef));

  return (
    <>
      <Head>
        <title>
          {title} by @{plate.snapshot.owner.username}
        </title>
      </Head>
      {!isScreenshotter && <PlateTopbar plate={plate} />}
      <ConditionalWrapper
        condition={isMobile}
        wrapper={(children: React.ReactNode) => <ZoomWrapper>{children}</ZoomWrapper>}
      >
        <ScrollContainer ref={scrollRef}>
          <OverflowAligner>
            <Container onMouseDown={(e) => plate.onMouseDown(e)}>
              <PrintOnlyTitle>{title}</PrintOnlyTitle>
              <ConditionalWrapper
                condition={editable && !isMobile}
                wrapper={(children: React.ReactNode) => <Uploader plate={plate}>{children}</Uploader>}
              >
                <Canvas plate={plate} ref={canvasRef}>
                  <Blocks plate={plate} />
                  <DragListener plate={plate} />
                  <Cursors plate={plate} color="red" />
                </Canvas>
              </ConditionalWrapper>
              {editable && (
                <StandardButton
                  onClick={() => plate.extend()}
                  styles={{ width: "200px", marginTop: "30px" }}
                  className="hide-on-print"
                >
                  Extend Plate
                </StandardButton>
              )}
            </Container>
          </OverflowAligner>
        </ScrollContainer>
      </ConditionalWrapper>
      {modal && (
        <Modal title={modal.title} close={() => plate.closeModal()}>
          {modal.body}
        </Modal>
      )}
      {/* {editable && !isMobile && <Terminal generator={plate.generator!} />} */}
      {editable && !isMobile && <Internals plate={plate} />}
      {editable && !isMobile && <HelpMenu />}
      {(drilledBlockId || drilledPageId) && <DrilledView />}
    </>
  );
}

const Container = styled.div`
  position: relative; // important
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 100px;
  user-select: none;
  padding: ${PLATE_PADDING}px;
  z-index: ${Layer.ROOT};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media screen and (max-width: ${DEVICE.small}) {
    padding-left: 0;
    padding-right: 0;
  }
`;

/**
 * Aligns plate left if viewport is narrower than the plate. All Properties are
 * required.
 */
const OverflowAligner = styled.div`
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
`;

const PrintOnlyTitle = styled.div`
  display: none;
  @media print {
    width: 100%;
    display: block;
    font-family: "IBM Plex Mono", monospace;
    font-weight: 600;
    font-size: 20px;
    margin-bottom: 20px;
    text-align: left;
  }
`;
